import { Lightning } from '@lightningjs/sdk'

import { getColor, grey50, grey600, grey960, transparent } from '../../constants/colors'
import { UniNeueRegular } from '../../constants/fonts'
import { BORDER_RADIUS } from '../../constants/general'
import { getText } from '../../utils/dictionary'
import { getIcon } from '../../utils/image'
import { DevSettingItem, toggleDevSetting } from '../../utils/settingsUtils'
import { Toggle } from '../Toggle/Toggle'

const SCALE = 1.08

export class DevSettingsItem extends Lightning.Component {
  private _isOn = false
  private _data: DevSettingItem
  private _settingIndex: number

  static _template() {
    return {
      w: 710,
      h: 137,
      rect: true,
      color: getColor(grey50),
      collision: true,
      shader: {
        type: Lightning.shaders.RoundedRectangle,
        stroke: 8,
        strokeColor: transparent,
        fillColor: getColor(grey50),
        radius: BORDER_RADIUS,
      },
      flex: { alignItems: 'center' },
      Icon: {
        color: getColor(grey960),
        shader: null,
        texture: null,
        visible: false,
        flexItem: { marginLeft: 32 },
      },
      Title: {
        y: 4,
        w: 408,
        shader: null,
        text: {
          text: '',
          fontSize: 32,
          fontFace: UniNeueRegular,
          textColor: getColor(grey960),
          wordWrapWidth: 408,
          textOverflow: 'ellipsis',
          maxLines: 2,
          maxLinesSuffix: '...',
        },
        flexItem: { marginLeft: 32 },
      },
      Toggle: {
        type: Toggle,
        visible: false,
      },
    }
  }

  get title() {
    return this.tag('Title')
  }

  get icon() {
    return this.tag('Icon')
  }

  get toggle() {
    return this.tag('Toggle') as Toggle
  }

  set data(v: DevSettingItem) {
    this._data = v
  }

  set settingIndex(i: number) {
    this._settingIndex = i
  }

  get settingIndex() {
    return this._settingIndex
  }

  _init() {
    this.renderItem()
  }

  _handleEnter() {
    if (this._data?.type === 'enable') {
      this.fireAncestors('$forceCloseApp', this._data)
    }

    if (this._data?.type === 'toggle') {
      this.toggleSetting()
    }
  }

  _focus() {
    this.focusItem(true)
  }

  _unfocus() {
    this.focusItem(false)
  }

  toggleSetting() {
    this._isOn = !this._isOn

    toggleDevSetting(this._data?.id, this._isOn)

    this.toggle.toggle(this._isOn)
  }

  getItemName(isOn = false) {
    return this._data?.type === 'enable'
      ? `${isOn ? getText('disable') : getText('enable')} ${this._data?.name}`
      : this._data?.name
  }

  renderItem() {
    this._isOn = this._data?.isOn

    this.title.patch({ text: { text: this.getItemName(this._data?.isOn) } })

    if (this._data?.icon) {
      this.icon.patch({
        visible: true,
        texture: Lightning.Tools.getSvgTexture(getIcon(this._data.icon), 42, 42),
      })
    }

    if (this._data?.type === 'toggle') {
      this.toggle.patch({
        visible: true,
        isOn: this._data?.isOn,
        flexItem: { marginLeft: this._data?.icon ? 64 : 138 },
      })
    }
  }

  focusItem(isFocused: boolean) {
    this.patch({
      shader: {
        strokeColor: isFocused ? getColor(grey600) : transparent,
      },
      smooth: { scale: isFocused ? SCALE : 1 },
    })
  }

  _handleHover() {
    this.fireAncestors('$updateFocusByHover', this)
    this._focus()
  }

  _handleUnhover() {
    this._unfocus()
  }

  _handleClick() {
    this._handleEnter()
  }
}
